import { z } from "zod";

import {
  filterSchema,
  isListComparator,
  upgradeLegacyFilter,
  type Filter,
  type FilterComparator,
  type FilterMatchMode,
} from "@/shared/domain/query/filters";

/**
 * Filter controls as persisted — one wire shape shared by saved Views
 * (`ViewDefinitionV1.filters`) and saved dashboards
 * (`dashboard_definition.filters`), mirroring the backend's
 * `roboto.query.SavedFilters`: snake_case `match_mode`, unrecognised keys
 * rejected (`extra="forbid"` there). UI state keeps `matchMode`, bare metric
 * paths, and the "is any of" / "is none of" rows, which the backend has no
 * comparator for, so the conversions below are the only place the two shapes
 * meet.
 *
 * A list row is saved as the equality row it means. `EQUALS` over several
 * values is any of them and `NOT_EQUALS` none of them, but neither ever
 * matches a field that is not set, so `or_null` says when the row does:
 *
 * | UI row                  | Saved row                      |
 * | ----------------------- | ------------------------------ |
 * | any of [a, b]           | `EQUALS [a, b]`                |
 * | any of [a, b, Not set]  | `EQUALS [a, b]`, `or_null`     |
 * | any of [Not set]        | `IS_NULL`                      |
 * | none of [a, b]          | `NOT_EQUALS [a, b]`, `or_null` |
 * | none of [a, b, Not set] | `NOT_EQUALS [a, b]`            |
 * | none of [Not set]       | `IS_NOT_NULL`                  |
 */
// Distributes over the `Filter` union, respelling each variant that has the key.
type Saved<F> = F extends unknown
  ? "includeNull" extends keyof F
    ? Omit<F, "includeNull"> & { or_null?: boolean }
    : F
  : never;

/**
 * One filter as persisted: a UI {@link Filter}, with `or_null` in place of
 * `includeNull` on the types whose lists can need it. The type keeps the UI's
 * comparators so a UI row's type carries over, but a saved row never holds
 * `ANY_OF` / `NONE_OF`: {@link savedFiltersSchema} rejects them.
 */
export type SavedFilter = Saved<Filter>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** The saved row a UI filter is written as: see the table above. */
function toSavedFilter(filter: Filter): SavedFilter {
  const { includeNull, ...rest } = filter as Filter & {
    includeNull?: boolean;
  };
  if (!isListComparator(rest.comparator)) {
    return rest;
  }
  const anyOf = rest.comparator === "ANY_OF";
  if (rest.values.length === 0 && includeNull === true) {
    return {
      ...rest,
      comparator: anyOf ? "IS_NULL" : "IS_NOT_NULL",
      values: [],
    } as SavedFilter;
  }
  const orNull = anyOf ? includeNull === true : includeNull !== true;
  return {
    ...rest,
    comparator: anyOf ? "EQUALS" : "NOT_EQUALS",
    ...(orNull ? { or_null: true } : {}),
  } as SavedFilter;
}

/**
 * The UI row a saved one stands for, the inverse of {@link toSavedFilter}.
 * A row without `or_null` reads through `upgradeLegacyFilter`: several values
 * of `EQUALS` as "any of", of `NOT_EQUALS` as "none of" with "Not set", since
 * `!=` leaves the unset rows out, and an enum's comparators as its list. So the
 * rows saved before the lists existed read the same way.
 *
 * Takes and returns raw input: it runs before validation.
 */
function fromSavedFilter(raw: unknown): unknown {
  if (!isRecord(raw)) {
    return raw;
  }
  const { or_null: orNull, ...row } = raw;
  if (orNull !== true) {
    return upgradeLegacyFilter(row);
  }
  return row.comparator === "EQUALS"
    ? { ...row, comparator: "ANY_OF", includeNull: true }
    : { ...row, comparator: "NONE_OF" };
}

/**
 * What a saved row must not hold, though the UI row it reads as could: a list
 * comparator, which exists only in UI state, and `or_null` off equality.
 */
function savedOnlyIssue(raw: unknown): string | undefined {
  if (!isRecord(raw) || typeof raw.comparator !== "string") {
    return undefined;
  }
  if (isListComparator(raw.comparator as FilterComparator)) {
    return `${raw.comparator} is not saved; save the equality row it means`;
  }
  if (
    raw.or_null === true &&
    raw.comparator !== "EQUALS" &&
    raw.comparator !== "NOT_EQUALS"
  ) {
    return "or_null applies only to EQUALS and NOT_EQUALS";
  }
  return undefined;
}

/**
 * Validates one persisted filter against the UI rules, by reading it as the
 * UI row it stands for, and outputs it in the saved shape again. Parsing is
 * idempotent: saved dashboards re-parse filters they already hold.
 */
const savedFilterSchema = z
  .unknown()
  .superRefine((raw, ctx) => {
    const issue = savedOnlyIssue(raw);
    if (issue !== undefined) {
      ctx.addIssue({ code: "custom", message: issue });
    }
  })
  .pipe(z.preprocess(fromSavedFilter, filterSchema))
  .transform(toSavedFilter);

export const savedFiltersSchema = z.strictObject({
  filters: z.array(savedFilterSchema),
  match_mode: z.enum(["AND", "OR"]).default("AND"),
});

export type SavedFilters = z.infer<typeof savedFiltersSchema>;

/**
 * Mirrors `METRIC_FIELD_PREFIX` in `roboto.query.filters`; the backend rejects
 * a bare metric path at parse time.
 */
const METRIC_FIELD_PREFIX = "metric.";

/**
 * Prefix a metric filter's field on the way out. The UI holds the bare path
 * (`navigation.path_deviation`) because that is what a chip shows; the stored
 * form carries the prefix so `field` means the same thing as in a `Condition`,
 * and a later migration to a query copies it across rather than special-casing
 * metrics.
 *
 * `filterToCondition` prefixes separately from bare UI state, so the query path
 * is unaffected.
 */
function toWireFilter(filter: Filter): Filter {
  return filter.type === "metric"
    ? { ...filter, field: `${METRIC_FIELD_PREFIX}${filter.field}` }
    : filter;
}

function fromWireFilter(filter: Filter): Filter {
  return filter.type === "metric" &&
    filter.field.startsWith(METRIC_FIELD_PREFIX)
    ? { ...filter, field: filter.field.slice(METRIC_FIELD_PREFIX.length) }
    : filter;
}

export function toSavedFilters(
  filters: Filter[],
  matchMode: FilterMatchMode,
): SavedFilters {
  return {
    filters: filters.map((filter) => toSavedFilter(toWireFilter(filter))),
    match_mode: matchMode,
  };
}

/**
 * The UI rows saved filters stand for. Reads them as they are, unvalidated:
 * a View's filters arrive as the API returned them.
 */
export function fromSavedFilters(saved: SavedFilters): {
  filters: Filter[];
  matchMode: FilterMatchMode;
} {
  return {
    filters: saved.filters.map((filter) =>
      fromWireFilter(fromSavedFilter(filter) as Filter),
    ),
    matchMode: saved.match_mode,
  };
}
